/**
 * Keeps toolbar / fixed popups inside the visible viewport.
 * Used by C2C/WebReader language selectors and toolbar dropdown menus.
 */

const DEFAULT_PADDING = 8;

const getViewportSize = () => {
  const vv = window.visualViewport;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
    offsetLeft: vv?.offsetLeft ?? 0,
    offsetTop: vv?.offsetTop ?? 0,
  };
};

/**
 * Compute fixed-position top/left for a popup anchored to a toolbar button,
 * clamped so a box of `width` x optional `height` stays on screen.
 */
export const getAnchoredDropdownPositionStyle = (anchorEl, {
  width = 260,
  height = null,
  gap = 6,
  padding = DEFAULT_PADDING,
} = {}) => {
  if (!anchorEl) {
    return {};
  }

  const rect = anchorEl.getBoundingClientRect();
  const { width: vw, height: vh, offsetLeft, offsetTop } = getViewportSize();
  const viewLeft = offsetLeft;
  const viewTop = offsetTop;
  const viewRight = offsetLeft + vw;
  const viewBottom = offsetTop + vh;

  let left = rect.left + rect.width / 2 - width / 2;
  left = Math.max(viewLeft + padding, Math.min(left, viewRight - width - padding));

  let top = rect.bottom + gap;
  if (height != null && height > 0) {
    if (top + height > viewBottom - padding) {
      const above = rect.top - gap - height;
      if (above >= viewTop + padding) {
        top = above;
      } else {
        top = Math.max(viewTop + padding, viewBottom - height - padding);
      }
    }
  }

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto',
    bottom: 'auto',
  };
};

/**
 * Shift an already-rendered element so its bounding box stays in the viewport.
 *
 * @param {HTMLElement} el
 * @param {{ padding?: number, preferTransform?: boolean, anchorEl?: HTMLElement|null, gap?: number }} options
 *   preferTransform — use translate() so CSS absolute menus keep their anchors
 *   anchorEl — when overflowing below, flip above this anchor if there is room
 */
export const keepElementInViewport = (el, {
  padding = DEFAULT_PADDING,
  preferTransform = false,
  anchorEl = null,
  gap = 6,
} = {}) => {
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return;
  }

  if (preferTransform) {
    el.style.transform = '';
  }

  const rect = el.getBoundingClientRect();
  const { width: vw, height: vh, offsetLeft, offsetTop } = getViewportSize();
  const viewLeft = offsetLeft;
  const viewTop = offsetTop;
  const viewRight = offsetLeft + vw;
  const viewBottom = offsetTop + vh;

  let dx = 0;
  let dy = 0;

  if (rect.right > viewRight - padding) {
    dx = (viewRight - padding) - rect.right;
  }
  if (rect.left + dx < viewLeft + padding) {
    dx = (viewLeft + padding) - rect.left;
  }

  if (rect.bottom > viewBottom - padding) {
    const anchorRect = anchorEl?.getBoundingClientRect?.();
    if (anchorRect) {
      const aboveTop = anchorRect.top - gap - rect.height;
      if (aboveTop >= viewTop + padding) {
        dy = aboveTop - rect.top;
      } else {
        dy = (viewBottom - padding) - rect.bottom;
      }
    } else {
      dy = (viewBottom - padding) - rect.bottom;
    }
  }
  if (rect.top + dy < viewTop + padding) {
    dy = (viewTop + padding) - rect.top;
  }

  if (!dx && !dy) {
    if (preferTransform) {
      el.style.transform = '';
    }
    return;
  }

  if (preferTransform) {
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    return;
  }

  const computed = window.getComputedStyle(el);
  if (computed.position === 'fixed' || computed.position === 'absolute') {
    const currentLeft = parseFloat(el.style.left);
    const currentTop = parseFloat(el.style.top);
    const baseLeft = Number.isFinite(currentLeft) ? currentLeft : rect.left;
    const baseTop = Number.isFinite(currentTop) ? currentTop : rect.top;
    el.style.left = `${baseLeft + dx}px`;
    el.style.top = `${baseTop + dy}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    return;
  }

  el.style.transform = `translate(${dx}px, ${dy}px)`;
};

export default {
  getAnchoredDropdownPositionStyle,
  keepElementInViewport,
};
